import { CanvasPreviewOptions } from './types';

export async function renderCanvasPreview(
  canvas: HTMLCanvasElement,
  imageUrl: string,
  options: CanvasPreviewOptions
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Load the main image
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = imageUrl;
  });

  // Create a temporary canvas for the image processing
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = img.width;
  tempCanvas.height = img.height;
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) return;

  // Clear the filter before initial draw
  tempCtx.filter = 'none';
  tempCtx.drawImage(img, 0, 0);

  // Apply basic adjustments first
  const adjustedCanvas = document.createElement('canvas');
  adjustedCanvas.width = img.width;
  adjustedCanvas.height = img.height;
  const adjustedCtx = adjustedCanvas.getContext('2d');
  if (!adjustedCtx) return;

  // Apply brightness, contrast, saturation, and hue
  adjustedCtx.filter = `brightness(${options.brightness}%) contrast(${options.contrast}%) saturate(${options.saturation}%) hue-rotate(${options.hue}deg)`;
  adjustedCtx.drawImage(tempCanvas, 0, 0);
  adjustedCtx.filter = 'none';

  // Apply sharpness if specified
  if (typeof options.sharpness === 'number' && options.sharpness !== 50) {
    const imageData = adjustedCtx.getImageData(0, 0, adjustedCanvas.width, adjustedCanvas.height);
    const data = imageData.data;
    const width = adjustedCanvas.width;
    const height = adjustedCanvas.height;
    
    // Create a copy of the image data
    const original = new Uint8ClampedArray(data);
    
    // Calculate sharpness intensity (convert 0-100 to appropriate kernel values)
    const intensity = (options.sharpness - 50) / 50; // Range: -1 to 1
    const kernel = [
      [0, -1, 0],
      [-1, 4 + intensity * 4, -1],
      [0, -1, 0]
    ];

    // Apply convolution
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // Only process non-transparent pixels
        if (original[idx + 3] > 0) {
          // Process each color channel
          for (let c = 0; c < 3; c++) {
            let sum = 0;
            
            // Apply kernel
            for (let ky = -1; ky <= 1; ky++) {
              for (let kx = -1; kx <= 1; kx++) {
                const offset = ((y + ky) * width + (x + kx)) * 4;
                sum += original[offset + c] * kernel[ky + 1][kx + 1];
              }
            }

            // Apply sharpening with intensity control
            const original_value = original[idx + c];
            data[idx + c] = Math.max(0, Math.min(255, 
              original_value + sum * Math.abs(intensity)
            ));
          }
        }
      }
    }

    adjustedCtx.putImageData(imageData, 0, 0);
  }

  // Calculate canvas size needed to contain rotated content
  const radians = (options.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  // Calculate bounding box for rotated rectangle
  const finalCanvasWidth = Math.ceil(img.width * cos + img.height * sin);
  const finalCanvasHeight = Math.ceil(img.width * sin + img.height * cos);

  // Always use a transform canvas so getNonTransparentBounds detects true content edges
  const transformCanvas = document.createElement('canvas');
  const transformCtx = transformCanvas.getContext('2d');
  if (!transformCtx) return;

  transformCanvas.width = finalCanvasWidth;
  transformCanvas.height = finalCanvasHeight;

  transformCtx.clearRect(0, 0, transformCanvas.width, transformCanvas.height);
  transformCtx.filter = 'none';

  transformCtx.save();
  transformCtx.translate(transformCanvas.width / 2, transformCanvas.height / 2);

  if (options.rotation !== 0) {
    const radians = (options.rotation * Math.PI) / 180;
    transformCtx.rotate(radians);
  }

  transformCtx.drawImage(
    adjustedCanvas,
    -adjustedCanvas.width / 2,
    -adjustedCanvas.height / 2
  );

  transformCtx.restore();
  const finalCanvas = transformCanvas;

  // Get the non-transparent pixels bounds
  const imageData = finalCanvas.getContext('2d')!.getImageData(0, 0, finalCanvas.width, finalCanvas.height);
  const bounds = getNonTransparentBounds(imageData);

  // Analyze product base for contact shadow
  const productBaseInfo = bounds ? analyzeProductBase(imageData, bounds) : null;

  // Apply auto-crop if enabled and bounds exist
  if (bounds && options.autoCrop) {
    // Calculate content dimensions
    const contentWidth = bounds.right - bounds.left;
    const contentHeight = bounds.bottom - bounds.top;
    
    // Calculate initial padding amount (user-specified percentage of the larger dimension)
    const maxContentDimension = Math.max(contentWidth, contentHeight);
    const paddingPercentage = (options.paddingAmount || 20) / 100;
    const initialPaddingAmount = maxContentDimension * paddingPercentage;
    
    // Determine which edges the content touches
    const touchesLeft = bounds.left === 0;
    const touchesRight = bounds.right === finalCanvas.width;
    const touchesTop = bounds.top === 0;
    const touchesBottom = bounds.bottom === finalCanvas.height;
    
    // Calculate initial padding for each side
    const initialPadLeft = touchesLeft ? 0 : initialPaddingAmount;
    const initialPadRight = touchesRight ? 0 : initialPaddingAmount;
    const initialPadTop = touchesTop ? 0 : initialPaddingAmount;
    const initialPadBottom = touchesBottom ? 0 : initialPaddingAmount;
    
    // Calculate the minimum square size needed to fit content with initial padding
    const minSquareSize = Math.max(
      contentWidth + initialPadLeft + initialPadRight,
      contentHeight + initialPadTop + initialPadBottom
    );
    
    // Set canvas to square dimensions
    canvas.width = minSquareSize;
    canvas.height = minSquareSize;
    
    // Calculate remaining space to distribute
    const horizontalRemaining = minSquareSize - (contentWidth + initialPadLeft + initialPadRight);
    const verticalRemaining = minSquareSize - (contentHeight + initialPadTop + initialPadBottom);
    
    // Distribute remaining space based on which edges content touches
    let finalPadLeft = initialPadLeft;
    let finalPadRight = initialPadRight;
    let finalPadTop = initialPadTop;
    let finalPadBottom = initialPadBottom;
    
    // Distribute horizontal remaining space
    if (touchesLeft && !touchesRight) {
      // Content touches left edge only, add all remaining to right
      finalPadRight += horizontalRemaining;
    } else if (!touchesLeft && touchesRight) {
      // Content touches right edge only, add all remaining to left
      finalPadLeft += horizontalRemaining;
    } else if (!touchesLeft && !touchesRight) {
      // Content touches neither edge, distribute evenly
      finalPadLeft += horizontalRemaining / 2;
      finalPadRight += horizontalRemaining / 2;
    }
    // If content touches both edges, no additional padding needed
    
    // Distribute vertical remaining space
    if (touchesTop && !touchesBottom) {
      // Content touches top edge only, add all remaining to bottom
      finalPadBottom += verticalRemaining;
    } else if (!touchesTop && touchesBottom) {
      // Content touches bottom edge only, add all remaining to top
      finalPadTop += verticalRemaining;
    } else if (!touchesTop && !touchesBottom) {
      // Content touches neither edge, distribute evenly
      finalPadTop += verticalRemaining / 2;
      finalPadBottom += verticalRemaining / 2;
    }
    // If content touches both edges, no additional padding needed

    // Clear and set background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    await setCanvasBackground(ctx, canvas.width, canvas.height, options.backgroundColor);

    // Apply contact shadow first if specified (needs to be drawn before the image)
    if (options.shadowType === 'contact' && productBaseInfo) {
      const baseWidth = productBaseInfo.baseWidth;
      const baseCenterRelative = productBaseInfo.baseCenter - bounds.left;
      const minShadowBaseWidth = contentWidth * 0.2;
      const effectiveWidth = Math.max(minShadowBaseWidth, Math.min(baseWidth, contentWidth * 0.8));

      const minAbsoluteShadowWidth = Math.max(contentWidth, contentHeight) * 0.35;
      const shadowWidth = Math.max(effectiveWidth * 2.2, minAbsoluteShadowWidth);
      const shadowHeight = Math.max(effectiveWidth * 0.06, contentHeight * 0.025, shadowWidth * 0.04);
      const shadowX = finalPadLeft + baseCenterRelative;
      const shadowY = finalPadTop + contentHeight + shadowHeight * 0.5;
      const blurAmount = Math.max(12, effectiveWidth * 0.04);

      ctx.save();
      ctx.translate(shadowX, shadowY);
      ctx.rotate((options.rotation * Math.PI) / 180);

      ctx.filter = `blur(${blurAmount}px)`;
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, shadowWidth / 2);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.28)');
      gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.18)');
      gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.08)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.ellipse(0, 0, shadowWidth / 2, shadowHeight / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.filter = 'none';
      ctx.restore();
    }

    // Note: Reflection shadow with auto-crop maintains square dimensions
    // The reflection will be drawn within the square canvas bounds below

    // Apply other shadow types if specified
    if (options.shadowType && options.shadowType !== 'none' && options.shadowType !== 'contact' && options.shadowType !== 'reflection') {
      const smallerDimension = Math.min(canvas.width, canvas.height);
      switch (options.shadowType) {
        case 'soft':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.05;
          break;
        case 'medium':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.04;
          break;
        case 'hard':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.03;
          break;
        case 'floating':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = smallerDimension * 0.08;
          ctx.shadowBlur = smallerDimension * 0.04;
          break;
      }
    }

    // Draw the image with calculated padding
    ctx.drawImage(
      finalCanvas,
      bounds.left, bounds.top, contentWidth, contentHeight,
      finalPadLeft, finalPadTop, contentWidth, contentHeight
    );

    // Draw reflection if specified
    if (options.shadowType === 'reflection') {
      const gap = 0;
      const reflectionStartY = finalPadTop + contentHeight + gap;

      // Calculate available space for reflection within the square canvas
      const availableReflectionSpace = canvas.height - reflectionStartY;

      // Only draw reflection if there's available space
      if (availableReflectionSpace > 0) {
        // Use available space for reflection height (limited by square canvas)
        const reflectionHeight = Math.min(contentHeight * 0.5, availableReflectionSpace);

        // Create a temporary canvas for the reflection
        const reflectionCanvas = document.createElement('canvas');
        reflectionCanvas.width = contentWidth;
        reflectionCanvas.height = reflectionHeight;
        const reflectionCtx = reflectionCanvas.getContext('2d');

        if (reflectionCtx) {
          // Draw the flipped image
          reflectionCtx.save();
          reflectionCtx.translate(0, reflectionHeight);
          reflectionCtx.scale(1, -1);

          // Draw portion of the original image (from bottom)
          reflectionCtx.drawImage(
            finalCanvas,
            bounds.left, bounds.top + contentHeight - reflectionHeight, contentWidth, reflectionHeight,
            0, 0, contentWidth, reflectionHeight
          );

          reflectionCtx.restore();

          // Apply gradient fade (30% to fully transparent)
          const gradient = reflectionCtx.createLinearGradient(0, 0, 0, reflectionHeight);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
          gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.85)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 1)')

          reflectionCtx.globalCompositeOperation = 'destination-out';
          reflectionCtx.fillStyle = gradient;
          reflectionCtx.fillRect(0, 0, contentWidth, reflectionHeight);

          // Draw the reflection onto main canvas
          ctx.drawImage(reflectionCanvas, finalPadLeft, reflectionStartY);
        }
      }
    }
  } else {
    canvas.width = finalCanvas.width;
    canvas.height = finalCanvas.height;

    // Clear and set background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    await setCanvasBackground(ctx, canvas.width, canvas.height, options.backgroundColor);

    // Apply contact shadow first if specified (needs to be drawn before the image)
    if (options.shadowType === 'contact' && productBaseInfo && bounds) {
      const baseWidth = productBaseInfo.baseWidth;
      const baseCenterX = productBaseInfo.baseCenter;
      const contentWidth = bounds.right - bounds.left;
      const contentHeight = bounds.bottom - bounds.top;
      const minShadowBaseWidth = contentWidth * 0.2;
      const effectiveWidth = Math.max(minShadowBaseWidth, Math.min(baseWidth, contentWidth * 0.8));

      const minAbsoluteShadowWidth = Math.max(contentWidth, contentHeight) * 0.35;
      const shadowWidth = Math.max(effectiveWidth * 2.2, minAbsoluteShadowWidth);
      const shadowHeight = Math.max(effectiveWidth * 0.06, contentHeight * 0.025, shadowWidth * 0.04);
      const shadowX = baseCenterX;
      const shadowY = bounds.bottom + shadowHeight * 0.5;
      const blurAmount = Math.max(12, effectiveWidth * 0.04);

      ctx.save();
      ctx.translate(shadowX, shadowY);
      ctx.rotate((options.rotation * Math.PI) / 180);

      ctx.filter = `blur(${blurAmount}px)`;
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, shadowWidth / 2);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.28)');
      gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.18)');
      gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.08)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.ellipse(0, 0, shadowWidth / 2, shadowHeight / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.filter = 'none';
      ctx.restore();
    }

    // Apply other shadow types
    if (options.shadowType && options.shadowType !== 'none' && options.shadowType !== 'contact' && options.shadowType !== 'reflection') {
      const smallerDimension = Math.min(canvas.width, canvas.height);
      switch (options.shadowType) {
        case 'soft':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.05;
          break;
        case 'medium':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.04;
          break;
        case 'hard':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
          ctx.shadowOffsetX = smallerDimension * 0.03;
          ctx.shadowOffsetY = smallerDimension * 0.03;
          ctx.shadowBlur = smallerDimension * 0.03;
          break;
        case 'floating':
          ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = smallerDimension * 0.08;
          ctx.shadowBlur = smallerDimension * 0.04;
          break;
      }
    }

    // Draw the final image at full size
    ctx.drawImage(finalCanvas, 0, 0);

    if (options.shadowType === 'reflection' && bounds) {
      const contentBottom = bounds.bottom;
      const contentWidth = bounds.right - bounds.left;
      const contentHeight = bounds.bottom - bounds.top;
      const availableSpace = canvas.height - contentBottom;

      if (availableSpace > 0) {
        const reflectionHeight = Math.min(contentHeight * 0.5, availableSpace);

        const reflectionCanvas = document.createElement('canvas');
        reflectionCanvas.width = contentWidth;
        reflectionCanvas.height = reflectionHeight;
        const reflectionCtx = reflectionCanvas.getContext('2d');

        if (reflectionCtx) {
          reflectionCtx.save();
          reflectionCtx.translate(0, reflectionHeight);
          reflectionCtx.scale(1, -1);

          reflectionCtx.drawImage(
            finalCanvas,
            bounds.left, bounds.top + contentHeight - reflectionHeight, contentWidth, reflectionHeight,
            0, 0, contentWidth, reflectionHeight
          );

          reflectionCtx.restore();

          const gradient = reflectionCtx.createLinearGradient(0, 0, 0, reflectionHeight);
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
          gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.85)');
          gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');

          reflectionCtx.globalCompositeOperation = 'destination-out';
          reflectionCtx.fillStyle = gradient;
          reflectionCtx.fillRect(0, 0, contentWidth, reflectionHeight);

          ctx.drawImage(reflectionCanvas, bounds.left, contentBottom);
        }
      }
    }
  }

  ctx.shadowColor = 'transparent';
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;

  if (options.targetSize && options.targetSize > 0) {
    const size = options.targetSize;
    const resizeCanvas = document.createElement('canvas');
    resizeCanvas.width = size;
    resizeCanvas.height = size;
    const resizeCtx = resizeCanvas.getContext('2d');
    if (resizeCtx) {
      resizeCtx.imageSmoothingEnabled = true;
      resizeCtx.imageSmoothingQuality = 'high';
      resizeCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, size, size);
      canvas.width = size;
      canvas.height = size;
      ctx.drawImage(resizeCanvas, 0, 0);
    }
  }
}

// Helper function to set canvas background (handles colors, gradients, and images)
async function setCanvasBackground(
  ctx: CanvasRenderingContext2D, 
  width: number, 
  height: number, 
  backgroundColor?: string | 'gradient'
): Promise<void> {
  if (!backgroundColor) return;

  if (backgroundColor === 'gradient') {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#000000');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  } else if (backgroundColor.startsWith('bg-gradient-')) {
    // Handle Tailwind gradient classes
    if (backgroundColor.includes('blue')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#60a5fa');
      gradient.addColorStop(1, '#2563eb');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('purple')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#a78bfa');
      gradient.addColorStop(1, '#7c3aed');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('orange')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#fb923c');
      gradient.addColorStop(1, '#ea580c');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('green')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#4ade80');
      gradient.addColorStop(1, '#16a34a');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('cyan') || backgroundColor.includes('ocean')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#67e8f9');
      gradient.addColorStop(0.5, '#3b82f6');
      gradient.addColorStop(1, '#2563eb');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('emerald') || backgroundColor.includes('forest')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#6ee7b7');
      gradient.addColorStop(0.5, '#10b981');
      gradient.addColorStop(1, '#059669');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('yellow') || backgroundColor.includes('warm')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#fde047');
      gradient.addColorStop(0.5, '#fb923c');
      gradient.addColorStop(1, '#ef4444');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (backgroundColor.includes('pink') || backgroundColor.includes('sunset')) {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#fed7aa');
      gradient.addColorStop(0.5, '#fb7185');
      gradient.addColorStop(1, '#a855f7');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }
  } else if (backgroundColor.startsWith('http') || backgroundColor.startsWith('https')) {
    // Handle image URLs
    try {
      const bgImg = new Image();
      bgImg.crossOrigin = 'anonymous'; // Important for loading images from other origins
      
      await new Promise((resolve, reject) => {
        bgImg.onload = resolve;
        bgImg.onerror = reject;
        bgImg.src = backgroundColor;
      });

      // Calculate aspect ratios
      const imgAspectRatio = bgImg.width / bgImg.height;
      const canvasAspectRatio = width / height;

      let sx = 0, sy = 0, sWidth = bgImg.width, sHeight = bgImg.height; // Source image dimensions
      let dx = 0, dy = 0, dWidth = width, dHeight = height; // Destination canvas dimensions

      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider than canvas, crop horizontally
        sWidth = bgImg.height * canvasAspectRatio;
        sx = (bgImg.width - sWidth) / 2;
      } else {
        // Image is taller than canvas, crop vertically
        sHeight = bgImg.width / canvasAspectRatio;
        sy = (bgImg.height - sHeight) / 2;
      }

      ctx.drawImage(bgImg, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);

    } catch (error) {
      console.warn('Failed to load background image:', error);
      // Fallback to white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
    }
  } else if (backgroundColor.startsWith('#')) {
    // Handle hex colors
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
  }
}

// Helper function to find the bounds of non-transparent pixels
function getNonTransparentBounds(imageData: ImageData) {
  const { width, height, data } = imageData;
  let left = width;
  let top = height;
  let right = 0;
  let bottom = 0;
  let hasContent = false;

  // Scan through all pixels
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 10) {
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x + 1);
        bottom = Math.max(bottom, y + 1);
        hasContent = true;
      }
    }
  }

  return hasContent ? { left, top, right, bottom } : null;
}

interface ProductBaseInfo {
  baseWidth: number;
  baseLeft: number;
  baseRight: number;
  baseCenter: number;
  productHeight: number;
  aspectRatio: number;
}

function analyzeProductBase(
  imageData: ImageData,
  bounds: { left: number; top: number; right: number; bottom: number }
): ProductBaseInfo {
  const { width, data } = imageData;
  const productHeight = bounds.bottom - bounds.top;
  const productWidth = bounds.right - bounds.left;

  const scanHeight = Math.min(Math.ceil(productHeight * 0.12), 30);
  const scanStartY = bounds.bottom - scanHeight;

  let baseLeft = bounds.right;
  let baseRight = bounds.left;

  for (let y = scanStartY; y < bounds.bottom; y++) {
    for (let x = bounds.left; x < bounds.right; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 20) {
        baseLeft = Math.min(baseLeft, x);
        baseRight = Math.max(baseRight, x);
      }
    }
  }

  if (baseLeft >= baseRight) {
    baseLeft = bounds.left;
    baseRight = bounds.right;
  }

  const baseWidth = baseRight - baseLeft;
  const baseCenter = baseLeft + baseWidth / 2;
  const aspectRatio = productHeight / productWidth;

  return {
    baseWidth,
    baseLeft,
    baseRight,
    baseCenter,
    productHeight,
    aspectRatio
  };
}